/**
 * BUSINESS LAYER (BL): MEMORAMA (MEMORY 1 VS 1)
 * Capa de lógica de negocio, validaciones, sintetizador Web Audio API nativo (0 KB / Cero MP3s),
 * feedback háptico, precarga proactiva de recursos gráficos a 60 FPS y persistencia de sesión móvil para Memorama.
 * 
 * @module utilities/BL/Memory
 */

import {
    GetActiveRoomDL,
    GetThemesDL,
    GetThemeByIdDL,
    CreateThemeDL,
    UpdateThemeDL,
    DeleteThemeDL,
    CloneThemeDL,
    GetConfigDL,
    SaveConfigDL,
    GetMatchHistoryDL,
    SaveMatchResultDL,
    UploadAssetDL,
    UploadBatchCardsDL,
    GetMatchStateDL,
    ShowCoverDL,
    ShowGameSceneDL,
    StartMatchDL,
    SelectPlayerSideDL,
    RegisterFanPlayerDL,
    FlipCardDL,
    UpdatePlayerNamesDL,
    AdjustScoreDL,
    ResetMatchDL,
    FinishMatchEarlyDL,
    LeaveGameDL,
    UnregisterFanPlayerDL,
    ClearPlayersDL,
    ChangeAccessPinDL
} from '../DL/Memory';

import { SetScreenV2MainDynamic } from './ScreenV2';

// =========================================================================
// SINTETIZADOR WEB AUDIO API NATIVO (0 KB / ZERO DESCARGAS MP3)
// =========================================================================

/**
 * Instancia singleton de AudioContext para Memorama.
 * @type {AudioContext|null}
 */
let memoryAudioCtx = null;

/**
 * Nodo maestro de ganancia para control de volumen y mute.
 * @type {GainNode|null}
 */
let memoryMasterGain = null;

/**
 * Bandera de silencio de audio global.
 * @type {boolean}
 */
let isMuted = false;

/**
 * Nivel de volumen antes de silenciar.
 * @type {number}
 */
let savedVolume = 0.7;

/**
 * Bandera para registrar si ya se intentó desbloquear el audio context con un gesto de usuario.
 * @type {boolean}
 */
let isAudioUnlocked = false;

/**
 * Comprueba si la API de Web Audio es soportada en el navegador actual.
 * 
 * @returns {boolean} True si AudioContext está disponible.
 */
export const isAudioSupported = () => {
    return typeof window !== 'undefined' && Boolean(window.AudioContext || window.webkitAudioContext);
};

/**
 * Obtiene o inicializa la instancia singleton de AudioContext para Memorama.
 * 
 * @returns {AudioContext|null} La instancia activa de AudioContext o null si no está soportado.
 */
export const getAudioContext = () => {
    if (!isAudioSupported()) return null;

    if (!memoryAudioCtx) {
        const AudioContextClass = window.AudioContext || window.webkitAudioContext;
        memoryAudioCtx = new AudioContextClass();

        memoryMasterGain = memoryAudioCtx.createGain();
        memoryMasterGain.gain.setValueAtTime(isMuted ? 0 : savedVolume, memoryAudioCtx.currentTime);
        memoryMasterGain.connect(memoryAudioCtx.destination);
    }

    return memoryAudioCtx;
};

/**
 * Desbloquea y reanuda de manera segura el AudioContext ante el primer gesto de usuario
 * (click, touchstart, pointerdown, keydown) cumpliendo con las políticas de autoplay de navegadores móviles y de escritorio.
 * 
 * @returns {Promise<boolean>} True si el AudioContext se encuentra en estado 'running'.
 */
export const unlockAudioContext = async () => {
    try {
        const ctx = getAudioContext();
        if (!ctx) return false;

        if (ctx.state === 'suspended') {
            await ctx.resume();
        }

        if (ctx.state === 'running') {
            isAudioUnlocked = true;
            return true;
        }
    } catch (e) {
        // Silencioso ante restricciones de interacción de navegadores
    }
    return false;
};

/** Alias de inicialización del sintetizador de audio */
export const initAudioSynth = unlockAudioContext;

/**
 * Auto-registra escuchas globales en window para desbloquear el AudioContext automáticamente en la primera interacción.
 */
if (typeof window !== 'undefined') {
    const handleFirstGesture = () => {
        unlockAudioContext().then(() => {
            if (isAudioUnlocked) {
                window.removeEventListener('pointerdown', handleFirstGesture);
                window.removeEventListener('touchstart', handleFirstGesture);
                window.removeEventListener('click', handleFirstGesture);
                window.removeEventListener('keydown', handleFirstGesture);
            }
        }).catch(() => {});
    };

    window.addEventListener('pointerdown', handleFirstGesture, { passive: true });
    window.addEventListener('touchstart', handleFirstGesture, { passive: true });
    window.addEventListener('click', handleFirstGesture, { passive: true });
    window.addEventListener('keydown', handleFirstGesture, { passive: true });
}

/**
 * Establece el estado de silencio para todos los efectos de sonido de Memorama.
 * 
 * @param {boolean} muted - True para silenciar, false para reactivar el audio.
 */
export const setAudioMuted = (muted) => {
    isMuted = Boolean(muted);
    const ctx = getAudioContext();
    if (ctx && memoryMasterGain) {
        const now = ctx.currentTime;
        memoryMasterGain.gain.cancelScheduledValues(now);
        memoryMasterGain.gain.setValueAtTime(isMuted ? 0 : savedVolume, now);
    }
};

/**
 * Consulta si los efectos de sonido se encuentran actualmente silenciados.
 * 
 * @returns {boolean} True si el audio está silenciado.
 */
export const isAudioMuted = () => isMuted;

/**
 * Alterna el estado de silencio de los efectos de audio.
 * 
 * @returns {boolean} Nuevo estado de silencio (true si silenciado, false si activo).
 */
export const toggleAudioMute = () => {
    setAudioMuted(!isMuted);
    return isMuted;
};

/**
 * Reproduce un tono sintetizado con envolvente de ganancia ADSR suave para evitar transitorios/pops.
 * 
 * @param {number} frequency - Frecuencia en Hertz (Hz).
 * @param {number} [durationMs=200] - Duración en milisegundos.
 * @param {('sine'|'triangle'|'sawtooth'|'square')} [waveform='sine'] - Tipo de onda del oscilador.
 * @param {number} [volume=0.3] - Ganancia pico (0.0 a 1.0).
 * @param {number} [attackMs=10] - Tiempo de ataque en ms.
 * @param {number} [releaseMs=50] - Tiempo de desvanecimiento en ms.
 * @returns {Promise<void>}
 */
export const playTone = async (
    frequency,
    durationMs = 200,
    waveform = 'sine',
    volume = 0.3,
    attackMs = 10,
    releaseMs = 50
) => {
    if (isMuted) return;

    const ctx = getAudioContext();
    if (!ctx) return;

    if (ctx.state === 'suspended') {
        await ctx.resume().catch(() => {});
    }
    if (ctx.state !== 'running') return;

    const now = ctx.currentTime;
    const durSec = Math.max(durationMs, 40) / 1000;
    const attackSec = Math.min(attackMs / 1000, durSec * 0.3);
    const releaseSec = Math.min(releaseMs / 1000, durSec * 0.4);

    const osc = ctx.createOscillator();
    osc.type = waveform;
    osc.frequency.setValueAtTime(frequency, now);

    const gainNode = ctx.createGain();
    const peakVolume = Math.max(Math.min(volume, 1.0), 0.01);

    gainNode.gain.setValueAtTime(0.0001, now);
    gainNode.gain.exponentialRampToValueAtTime(peakVolume, now + attackSec);
    gainNode.gain.setValueAtTime(peakVolume, now + (durSec - releaseSec));
    gainNode.gain.exponentialRampToValueAtTime(0.0001, now + durSec);

    osc.connect(gainNode);
    gainNode.connect(memoryMasterGain || ctx.destination);

    try {
        osc.start(now);
        osc.stop(now + durSec + 0.02);

        setTimeout(() => {
            try {
                osc.disconnect();
                gainNode.disconnect();
            } catch (e) {}
        }, durationMs + 80);
    } catch (e) {
        // Silencioso ante interrupciones de contexto
    }
};

/**
 * Reproduce un sonido de "Flip" percusivo corto y orgánico cuando una carta es volteada.
 * Genera un barrido de frecuencia ascendente rápido (240Hz -> 580Hz) con envolvente ágil de 60ms.
 * 
 * @param {number} [volume=0.3] - Nivel de volumen del efecto (0.0 a 1.0).
 * @returns {Promise<void>}
 */
export const playFlipSound = async (volume = 0.3) => {
    if (isMuted) return;
    const ctx = getAudioContext();
    if (!ctx) return;

    if (ctx.state === 'suspended') {
        await ctx.resume().catch(() => {});
    }
    if (ctx.state !== 'running') return;

    const now = ctx.currentTime;
    const durSec = 0.065; // 65ms

    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(240, now);
    osc.frequency.exponentialRampToValueAtTime(580, now + durSec);

    const gainNode = ctx.createGain();
    const peakVol = Math.max(Math.min(volume, 1.0), 0.01);

    gainNode.gain.setValueAtTime(0.0001, now);
    gainNode.gain.exponentialRampToValueAtTime(peakVol, now + 0.008);
    gainNode.gain.exponentialRampToValueAtTime(0.0001, now + durSec);

    osc.connect(gainNode);
    gainNode.connect(memoryMasterGain || ctx.destination);

    try {
        osc.start(now);
        osc.stop(now + durSec + 0.01);

        setTimeout(() => {
            try {
                osc.disconnect();
                gainNode.disconnect();
            } catch (e) {}
        }, 120);
    } catch (e) {}
};

/**
 * Reproduce un arpegio mayor festivo ascendente de 3 notas brillantes ante un acierto de par (Match Success).
 * Notas: C5 (523.25 Hz) -> E5 (659.25 Hz) -> G5 (783.99 Hz) con armónicos triangulares cálidos.
 * 
 * @param {number} [volume=0.4] - Nivel de volumen del efecto (0.0 a 1.0).
 * @returns {Promise<void>}
 */
export const playMatchSuccessSound = async (volume = 0.4) => {
    if (isMuted) return;

    const notes = [
        { freq: 523.25, delayMs: 0, durMs: 140 },  // C5 (Do)
        { freq: 659.25, delayMs: 80, durMs: 150 }, // E5 (Mi)
        { freq: 783.99, delayMs: 160, durMs: 320 } // G5 (Sol sostenido brillante)
    ];

    notes.forEach((n) => {
        setTimeout(() => {
            playTone(n.freq, n.durMs, 'triangle', volume, 10, 60);
        }, n.delayMs);
    });
};

/**
 * Reproduce un tono descendente suave y percusivo ante un fallo de par (Match Failed / No Match).
 * Genera una caída armónica de 340Hz a 190Hz para dar retroalimentación sutil y no frustrante.
 * 
 * @param {number} [volume=0.3] - Nivel de volumen del efecto (0.0 a 1.0).
 * @returns {Promise<void>}
 */
export const playMatchFailedSound = async (volume = 0.3) => {
    if (isMuted) return;
    const ctx = getAudioContext();
    if (!ctx) return;

    if (ctx.state === 'suspended') {
        await ctx.resume().catch(() => {});
    }
    if (ctx.state !== 'running') return;

    const now = ctx.currentTime;
    const durSec = 0.18; // 180ms

    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(340, now);
    osc.frequency.exponentialRampToValueAtTime(190, now + durSec);

    const gainNode = ctx.createGain();
    const peakVol = Math.max(Math.min(volume, 1.0), 0.01);

    gainNode.gain.setValueAtTime(0.0001, now);
    gainNode.gain.exponentialRampToValueAtTime(peakVol, now + 0.012);
    gainNode.gain.exponentialRampToValueAtTime(0.0001, now + durSec);

    osc.connect(gainNode);
    gainNode.connect(memoryMasterGain || ctx.destination);

    try {
        osc.start(now);
        osc.stop(now + durSec + 0.01);

        setTimeout(() => {
            try {
                osc.disconnect();
                gainNode.disconnect();
            } catch (e) {}
        }, 220);
    } catch (e) {}
};

/**
 * Reproduce una fanfarria triunfal de victoria cuando un jugador o equipo gana la partida de Memorama.
 * Secuencia: C5 (523Hz) -> E5 (659Hz) -> G5 (784Hz) -> C6 (1046Hz) con acorde final resonante.
 * 
 * @param {number} [volume=0.45] - Nivel de volumen de la fanfarria (0.0 a 1.0).
 * @returns {Promise<void>}
 */
export const playVictoryFanfare = async (volume = 0.45) => {
    if (isMuted) return;

    const notes = [
        { freq: 523.25, delayMs: 0, durMs: 160 },    // C5
        { freq: 659.25, delayMs: 120, durMs: 160 },  // E5
        { freq: 783.99, delayMs: 240, durMs: 200 },  // G5
        { freq: 1046.50, delayMs: 400, durMs: 650 }  // C6 (Clímax)
    ];

    notes.forEach((n) => {
        setTimeout(() => {
            playTone(n.freq, n.durMs, 'triangle', volume, 12, 100);
        }, n.delayMs);
    });

    // Acorde armónico de fondo sostenido (C5 + G5 + C6)
    setTimeout(() => {
        playTone(523.25, 600, 'sine', volume * 0.35, 20, 150);
        playTone(783.99, 600, 'sine', volume * 0.35, 20, 150);
    }, 420);
};

/**
 * Reproduce un bip sincronizado para el conteo regresivo 3..2..1..YA.
 * 
 * @param {boolean} [isFinal=false] - True para el tono final de inicio (más agudo).
 * @param {number} [volume=0.35] - Volumen del bip.
 * @returns {Promise<void>}
 */
export const playCountdownBeep = async (isFinal = false, volume = 0.35) => {
    const freq = isFinal ? 880.00 : 440.00;
    const duration = isFinal ? 350 : 180;
    return await playTone(freq, duration, 'sine', volume, 8, 40);
};

// =========================================================================
// RETROALIMENTACIÓN HÁPTICA (VIBRATION API)
// =========================================================================

/**
 * Dispara un patrón de vibración háptica en dispositivos móviles compatibles.
 * Posee fallback silencioso automático en navegadores o entornos de escritorio donde no esté soportado.
 * 
 * @param {number|Array<number>} [pattern=30] - Duración en ms o array de pulsos y pausas (ej. [40, 50, 60]).
 * @returns {boolean} True si el patrón fue despachado al dispositivo.
 */
export const triggerHapticFeedback = (pattern = 30) => {
    try {
        if (typeof window !== 'undefined' && typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
            return navigator.vibrate(pattern);
        }
    } catch (err) {
        // Fallback silencioso
    }
    return false;
};

/** Vibración corta y sutil al voltear una carta */
export const hapticFlip = () => triggerHapticFeedback(25);

/** Vibración festiva ante un acierto de par */
export const hapticMatchSuccess = () => triggerHapticFeedback([40, 40, 60]);

/** Vibración doble de error ante un fallo de par */
export const hapticMatchFailed = () => triggerHapticFeedback([60, 40, 60]);

/** Vibración triunfal de victoria */
export const hapticVictory = () => triggerHapticFeedback([100, 50, 100, 50, 200]);

// =========================================================================
// PRE-CARGA PROACTIVA DE RECURSOS GRÁFICOS (60 FPS / CERO PANTALLAS BLANCAS)
// =========================================================================

/**
 * Extrae y normaliza una lista de URLs de un campo que puede ser string JSON, Array de strings u objetos.
 * 
 * @param {string|Array<any>|null} deckImagesField - Campo con imágenes de baraja.
 * @returns {Array<string>} Lista limpia de URLs de imágenes.
 */
const extractDeckUrls = (deckImagesField) => {
    if (!deckImagesField) return [];

    let rawArray = deckImagesField;
    if (typeof deckImagesField === 'string') {
        try {
            rawArray = JSON.parse(deckImagesField);
        } catch (e) {
            rawArray = deckImagesField.split(',').map(s => s.trim()).filter(Boolean);
        }
    }

    if (!Array.isArray(rawArray)) return [];

    const urls = [];
    rawArray.forEach((item) => {
        if (typeof item === 'string' && item.trim()) {
            urls.push(item.trim());
        } else if (item && typeof item === 'object') {
            const url = item.url || item.imageUrl || item.src;
            if (typeof url === 'string' && url.trim()) {
                urls.push(url.trim());
            }
        }
    });

    return urls;
};

/**
 * Resuelve rutas de archivos multimedia a URLs absolutas completas.
 * Soporta resolución dinámica de hostname en redes locales (LAN) para dispositivos móviles.
 * 
 * @param {string|null} mediaPath - Ruta relativa o URL absoluta.
 * @returns {string} URL absoluta del recurso.
 */
export const resolveMediaUrl = (mediaPath) => {
    if (!mediaPath) return '';
    if (
        mediaPath.startsWith('http://') ||
        mediaPath.startsWith('https://') ||
        mediaPath.startsWith('blob:') ||
        mediaPath.startsWith('data:')
    ) {
        return mediaPath;
    }
    let apiBaseEndpointUrl = (process.env.REACT_APP_API_URL || 'https://localhost:50586/api/').replace('/api/', '/').replace('/api', '/');
    
    // Si estamos en un dispositivo móvil/navegador en red local y hostname no es localhost:
    if (typeof window !== 'undefined' && window.location && window.location.hostname && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
        if (apiBaseEndpointUrl.includes('localhost') || apiBaseEndpointUrl.includes('127.0.0.1')) {
            apiBaseEndpointUrl = apiBaseEndpointUrl.replace('localhost', window.location.hostname).replace('127.0.0.1', window.location.hostname);
        }
        if (window.location.protocol === 'http:' && apiBaseEndpointUrl.startsWith('https://')) {
            apiBaseEndpointUrl = apiBaseEndpointUrl.replace('https://', 'http://');
        }
    }

    const sanitizedMediaPath = mediaPath.startsWith('/') ? mediaPath.substring(1) : mediaPath;
    return `${apiBaseEndpointUrl}${sanitizedMediaPath}`;
};

export const getResolvedMediaUrl = resolveMediaUrl;

/**
 * Precarga proactivamente en la memoria caché del navegador todas las imágenes de un tema de Memorama
 * (fondos de portada/juego/aficionado, logos de patrocinio, reverso de cartas, avatares y barajas compartidas o individuales).
 * Garantiza transiciones fluidas a 60 FPS sin parpadeos ni pantallas blancas.
 * 
 * @param {Object} theme - Datos completos del tema visual de Memorama.
 * @returns {Promise<{total: number, loaded: number, failed: number, urls: Array<string>}>} Métricas de precarga.
 */
export const preloadImages = (theme) => {
    return new Promise((resolve) => {
        if (!theme || typeof theme !== 'object') {
            resolve({ total: 0, loaded: 0, failed: 0, urls: [] });
            return;
        }

        const urlSet = new Set();

        const addIfValid = (val) => {
            if (typeof val === 'string' && val.trim().length > 3) {
                urlSet.add(resolveMediaUrl(val.trim()));
            }
        };

        // 1. Fondos
        addIfValid(theme.CoverBackgroundUrl || theme.coverBackgroundUrl);
        addIfValid(theme.GameBackgroundUrl || theme.gameBackgroundUrl);
        addIfValid(theme.FanBackgroundUrl || theme.fanBackgroundUrl);

        // 2. Logos y reversos
        addIfValid(theme.BrandLogoUrl || theme.brandLogoUrl);
        addIfValid(theme.SponsorLogoUrl || theme.sponsorLogoUrl);
        addIfValid(theme.CardBackLogoUrl || theme.cardBackLogoUrl);

        // 3. Avatares
        addIfValid(theme.Player1Avatar || theme.player1Avatar);
        addIfValid(theme.Player1Winner || theme.player1Winner);
        addIfValid(theme.Player2Avatar || theme.player2Avatar);
        addIfValid(theme.Player2Winner || theme.player2Winner);

        // 4. Barajas
        const sharedUrls = extractDeckUrls(theme.SharedDeckImages || theme.sharedDeckImages);
        const p1Urls = extractDeckUrls(theme.DeckImagesP1 || theme.deckImagesP1);
        const p2Urls = extractDeckUrls(theme.DeckImagesP2 || theme.deckImagesP2);

        sharedUrls.forEach(u => addIfValid(u));
        p1Urls.forEach(u => addIfValid(u));
        p2Urls.forEach(u => addIfValid(u));

        const allUrls = Array.from(urlSet).filter(u => typeof u === 'string' && u.length > 5);

        if (allUrls.length === 0) {
            resolve({ total: 0, loaded: 0, failed: 0, urls: [] });
            return;
        }

        let loadedCount = 0;
        let failedCount = 0;
        const total = allUrls.length;

        const promises = allUrls.map((url) => {
            return new Promise((imgResolve) => {
                if (typeof window === 'undefined' || !window.Image) {
                    imgResolve({ url, success: true });
                    return;
                }

                const img = new Image();
                img.onload = () => {
                    loadedCount++;
                    imgResolve({ url, success: true });
                };
                img.onerror = () => {
                    failedCount++;
                    imgResolve({ url, success: false });
                };
                img.src = url;
            });
        });

        Promise.all(promises).then(() => {
            resolve({
                total,
                loaded: loadedCount,
                failed: failedCount,
                urls: allUrls
            });
        });
    });
};

// =========================================================================
// HELPERS DE PERSISTENCIA MÓVIL (SESSION STORAGE)
// =========================================================================

/**
 * Obtiene la información del jugador/rol almacenada en la sesión del aficionado móvil para una sala.
 * 
 * @param {string|null} [roomId=null] - Identificador opcional de sala.
 * @returns {{playerChoice: string, playerName: string, roomId: string}|null} Datos del aficionado o null.
 */
export const getStoredFanPlayer = (roomId = null) => {
    try {
        if (typeof window === 'undefined' || !window.sessionStorage) return null;

        const key = roomId ? `memory_fan_player_${roomId}` : 'memory_fan_player';
        const raw = sessionStorage.getItem(key) || sessionStorage.getItem('memory_fan_player');
        if (!raw) return null;

        return JSON.parse(raw);
    } catch (e) {
        return null;
    }
};

/**
 * Guarda la elección de personaje y nombre del aficionado móvil en el sessionStorage de la sala.
 * 
 * @param {string} roomId - Identificador GUID de la sala.
 * @param {string} playerChoice - Rol seleccionado ("player1" o "player2").
 * @param {string} [playerName=''] - Nombre del participante.
 */
export const setStoredFanPlayer = (roomId, playerChoice, playerName = '') => {
    try {
        if (typeof window === 'undefined' || !window.sessionStorage) return;

        const payload = {
            roomId: String(roomId || ''),
            playerChoice: String(playerChoice || 'player1'),
            playerName: String(playerName || '').trim(),
            savedAt: Date.now()
        };

        const key = roomId ? `memory_fan_player_${roomId}` : 'memory_fan_player';
        sessionStorage.setItem(key, JSON.stringify(payload));
        sessionStorage.setItem('memory_fan_player', JSON.stringify(payload));
    } catch (e) {}
};

/**
 * Limpia la información del jugador del sessionStorage.
 * 
 * @param {string|null} [roomId=null] - Identificador de sala.
 */
export const clearStoredFanPlayer = (roomId = null) => {
    try {
        if (typeof window === 'undefined' || !window.sessionStorage) return;

        if (roomId) {
            sessionStorage.removeItem(`memory_fan_player_${roomId}`);
        }
        sessionStorage.removeItem('memory_fan_player');
    } catch (e) {}
};

// =========================================================================
// BUSINESS LOGIC: GESTIÓN DE TEMAS Y BARAJAS
// =========================================================================

/**
 * Obtiene el catálogo de temas visuales disponibles para Memorama.
 * 
 * @param {string|null} [roomId=null] - ID de sala.
 * @param {string|null} [operatorId=null] - ID de operador.
 * @returns {Promise<{success: boolean, data: Array<Object>, message?: string}>} Colección de temas.
 */
export const GetThemes = async (roomId = null, operatorId = null) => {
    try {
        const res = await GetThemesDL(roomId, operatorId);
        if (!res.success) {
            return {
                success: false,
                data: [],
                message: res.message || 'No fue posible obtener los temas de Memorama.'
            };
        }

        const themesArray = Array.isArray(res.data) ? res.data : (res.data?.themes || []);
        return {
            success: true,
            data: themesArray,
            message: res.message
        };
    } catch (error) {
        return {
            success: false,
            data: [],
            message: 'Error al consultar los temas de Memorama.'
        };
    }
};

/**
 * Obtiene el detalle y configuración completa de un tema de Memorama por su ID.
 * 
 * @param {number|string} id - ID del tema.
 * @param {string|null} [roomId=null] - ID opcional de sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Detalle del tema.
 */
export const GetThemeById = async (id, roomId = null) => {
    try {
        if (!id) {
            return { success: false, data: null, message: 'El ID del tema es obligatorio.' };
        }
        return await GetThemeByIdDL(id, roomId);
    } catch (error) {
        return {
            success: false,
            data: null,
            message: 'Error al obtener el tema de Memorama.'
        };
    }
};

/** Alias de GetThemeById */
export const GetTheme = GetThemeById;

/**
 * Guarda o actualiza un tema visual de Memorama tras validar y normalizar su estructura.
 * 
 * @param {Object} themeData - Datos del tema a persistir.
 * @param {string|null} [roomId=null] - ID de sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Tema guardado.
 */
export const SaveTheme = async (themeData, roomId = null) => {
    try {
        if (!themeData || !themeData.name || !themeData.name.trim()) {
            return {
                success: false,
                data: null,
                message: 'El nombre del tema es obligatorio.'
            };
        }

        const sanitizeGuidOrNull = (value) => {
            if (!value || typeof value !== 'string') return null;
            const trimmed = value.trim();
            if (!trimmed) return null;
            const guidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
            return guidRegex.test(trimmed) ? trimmed : null;
        };

        const resolvedRoomId = sanitizeGuidOrNull(themeData.roomId) || sanitizeGuidOrNull(roomId) || null;
        const resolvedOperatorId = sanitizeGuidOrNull(themeData.operatorId) || null;

        const normalizedTheme = {
            ...themeData,
            name: themeData.name.trim(),
            roomId: resolvedRoomId,
            operatorId: resolvedOperatorId,
            sharedDeckImages: typeof themeData.sharedDeckImages === 'string'
                ? themeData.sharedDeckImages
                : JSON.stringify(themeData.sharedDeckImages || []),
            deckImagesP1: typeof themeData.deckImagesP1 === 'string'
                ? themeData.deckImagesP1
                : JSON.stringify(themeData.deckImagesP1 || []),
            deckImagesP2: typeof themeData.deckImagesP2 === 'string'
                ? themeData.deckImagesP2
                : JSON.stringify(themeData.deckImagesP2 || [])
        };

        if (normalizedTheme.id && Number(normalizedTheme.id) > 0) {
            return await UpdateThemeDL(Number(normalizedTheme.id), normalizedTheme, resolvedRoomId);
        } else {
            return await CreateThemeDL(normalizedTheme, resolvedRoomId);
        }
    } catch (error) {
        return {
            success: false,
            data: null,
            message: 'Error al guardar el tema de Memorama.'
        };
    }
};

/**
 * Crea un nuevo tema visual para Memorama.
 * 
 * @param {Object} themeData - Datos del nuevo tema.
 * @param {string|null} [roomId=null] - ID de sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Tema creado.
 */
export const CreateTheme = async (themeData, roomId = null) => {
    return await SaveTheme({ ...themeData, id: 0 }, roomId);
};

/**
 * Actualiza la configuración de un tema existente de Memorama.
 * 
 * @param {number|string} id - ID del tema.
 * @param {Object} themeData - Datos actualizados.
 * @param {string|null} [roomId=null] - ID de sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Tema actualizado.
 */
export const UpdateTheme = async (id, themeData, roomId = null) => {
    if (!id) {
        return { success: false, data: null, message: 'El ID del tema es requerido para actualizar.' };
    }
    return await SaveTheme({ ...themeData, id: Number(id) }, roomId);
};

/**
 * Elimina un tema personalizado de Memorama.
 * 
 * @param {number|string} id - ID del tema a eliminar.
 * @param {string|null} [roomId=null] - ID de sala.
 * @returns {Promise<{success: boolean, message?: string}>} Confirmación de eliminación.
 */
export const DeleteTheme = async (id, roomId = null) => {
    try {
        if (!id) {
            return { success: false, message: 'El ID del tema a eliminar es obligatorio.' };
        }
        return await DeleteThemeDL(id, roomId);
    } catch (error) {
        return {
            success: false,
            message: 'Error al eliminar el tema de Memorama.'
        };
    }
};

/**
 * Clona un tema existente de Memorama como una nueva copia editable.
 * 
 * @param {number|string} id - ID del tema origen.
 * @param {string|null} [newName=null] - Nombre opcional para la copia.
 * @param {string|null} [roomId=null] - ID de sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Tema clonado.
 */
export const CloneTheme = async (id, newName = null, roomId = null) => {
    try {
        if (!id) {
            return { success: false, data: null, message: 'El ID del tema origen es obligatorio.' };
        }
        return await CloneThemeDL(id, newName, roomId);
    } catch (error) {
        return {
            success: false,
            data: null,
            message: 'Error al clonar el tema de Memorama.'
        };
    }
};

// =========================================================================
// BUSINESS LOGIC: CONFIGURACIÓN DE SALA
// =========================================================================
// BUSINESS LOGIC: SALA Y CONFIGURACIÓN
// =========================================================================

/**
 * Obtiene el identificador de la sala activa por defecto en el sistema.
 * 
 * @returns {Promise<{success: boolean, data?: {roomId: string}, message?: string}>} Sala activa resuelta.
 */
export const GetActiveRoom = async () => {
    try {
        return await GetActiveRoomDL();
    } catch (error) {
        return {
            success: false,
            data: null,
            message: 'Error al consultar la sala activa de Memorama.'
        };
    }
};

/**
 * Obtiene la configuración activa de Memorama para una sala específica.
 * 
 * @param {string|null} [roomId=null] - Identificador GUID de la sala o null para sala activa.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Configuración activa.
 */
export const GetConfig = async (roomId = null) => {
    try {
        return await GetConfigDL(roomId);
    } catch (error) {
        return {
            success: false,
            data: null,
            message: 'Error al consultar la configuración de Memorama.'
        };
    }
};

/**
 * Guarda y normaliza los parámetros de configuración de Memorama para una sala.
 * 
 * @param {Object} configData - Configuración.
 * @param {string|null} [configData.roomId=null] - ID de sala.
 * @param {number|null} [configData.activeThemeId=null] - Tema activo.
 * @param {number} [configData.roundDurationSeconds=60] - Tiempo de ronda en segundos (15 a 300).
 * @param {number} [configData.pairsCount=6] - Pares a buscar (3 a 12).
 * @param {string} [configData.tieBreakMode='least_flips'] - Desempate ('least_flips' | 'pure_tie' | 'fastest_match').
 * @param {number} [configData.previewTimeSeconds=3] - Tiempo de memorización en segundos (0 a 10).
 * @param {number} [configData.hideUnmatchedDelayMs=800] - Retardo de volteo erróneo en ms (400 a 2000).
 * @param {string|null} [roomId=null] - ID opcional de sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Configuración persistida.
 */
export const SaveConfig = async (configData, roomId = null) => {
    try {
        const effectiveRoomId = roomId || configData?.roomId || null;

        const normalizedConfig = {
            ...configData,
            roomId: effectiveRoomId ? String(effectiveRoomId) : null,
            activeThemeId: configData.activeThemeId ? Number(configData.activeThemeId) : null,
            roundDurationSeconds: Math.min(Math.max(Number(configData.roundDurationSeconds) || 60, 15), 300),
            pairsCount: Math.min(Math.max(Number(configData.pairsCount) || 6, 3), 12),
            tieBreakMode: ['least_flips', 'pure_tie', 'fastest_match'].includes(configData.tieBreakMode)
                ? configData.tieBreakMode
                : 'least_flips',
            previewTimeSeconds: Math.min(Math.max(Number(configData.previewTimeSeconds ?? 3), 0), 10),
            hideUnmatchedDelayMs: Math.min(Math.max(Number(configData.hideUnmatchedDelayMs ?? 800), 400), 2000)
        };

        return await SaveConfigDL(normalizedConfig, effectiveRoomId);
    } catch (error) {
        return {
            success: false,
            data: null,
            message: 'Error al guardar la configuración de Memorama.'
        };
    }
};

// =========================================================================
// BUSINESS LOGIC: HISTORIAL Y PERSISTENCIA
// =========================================================================

/**
 * Obtiene el historial de partidas disputadas en Memorama para una sala.
 * 
 * @param {string|null} [roomId=null] - ID de sala.
 * @param {number} [limit=20] - Límite de partidas.
 * @returns {Promise<{success: boolean, data: Array<Object>, message?: string}>} Colección de partidas.
 */
export const GetMatchHistory = async (roomId = null, limit = 20) => {
    try {
        return await GetMatchHistoryDL(roomId, limit);
    } catch (error) {
        return {
            success: false,
            data: [],
            message: 'Error al consultar el historial de Memorama.'
        };
    }
};

/**
 * Registra y persiste las métricas y resultado final de una partida de Memorama.
 * 
 * @param {Object} matchData - Métricas de la partida.
 * @param {string|null} [roomId=null] - ID opcional de sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Resultado guardado.
 */
export const SaveMatchResult = async (matchData, roomId = null) => {
    try {
        const effectiveRoomId = roomId || matchData?.roomId || null;
        return await SaveMatchResultDL(matchData, effectiveRoomId);
    } catch (error) {
        return {
            success: false,
            data: null,
            message: 'Error al persistir el resultado de la partida de Memorama.'
        };
    }
};

// =========================================================================
// BUSINESS LOGIC: SUBIDA DE RECURSOS
// =========================================================================

/**
 * Sube y optimiza una imagen para Memorama.
 * 
 * @param {File|Blob|FormData} file - Imagen a transmitir.
 * @param {string|null} [roomId=null] - ID opcional de sala.
 * @returns {Promise<{success: boolean, url?: string, data?: Object, message?: string}>} URL pública generada.
 */
export const UploadAsset = async (file, roomId = null) => {
    try {
        if (!file) {
            return { success: false, url: '', message: 'No se ha seleccionado ningún archivo para subir.' };
        }
        return await UploadAssetDL(file, roomId);
    } catch (error) {
        return {
            success: false,
            url: '',
            message: 'Error al procesar la subida del recurso de Memorama.'
        };
    }
};

/**
 * Sube un lote de imágenes para barajas de Memorama.
 * 
 * @param {Array<File>|FileList|FormData} files - Lote de imágenes.
 * @param {string|null} [roomId=null] - ID de sala.
 * @returns {Promise<{success: boolean, urls?: Array<string>, data?: Object, message?: string}>} URLs generadas.
 */
export const UploadBatchCards = async (files, roomId = null) => {
    try {
        if (!files || (Array.isArray(files) && files.length === 0)) {
            return { success: false, urls: [], message: 'No se recibieron archivos para procesar en lote.' };
        }
        return await UploadBatchCardsDL(files, roomId);
    } catch (error) {
        return {
            success: false,
            urls: [],
            message: 'Error al procesar la subida masiva de imágenes de Memorama.'
        };
    }
};

// =========================================================================
// BUSINESS LOGIC: CONTROL DE PARTIDA EN TIEMPO REAL
// =========================================================================

/**
 * Consulta el estado en vivo de la partida de Memorama en una sala con blindaje Zero-Knowledge.
 * 
 * @param {string|null} [roomId=null] - ID de la sala.
 * @param {string|null} [forSide=null] - Lado opcional del jugador ("player1" o "player2").
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado en vivo.
 */
export const GetMatchState = async (roomId = null, forSide = null) => {
    try {
        return await GetMatchStateDL(roomId, forSide);
    } catch (error) {
        return {
            success: false,
            data: null,
            message: 'Error al consultar el estado de la partida de Memorama.'
        };
    }
};

/**
 * Muestra la portada de Memorama en la pantalla gigante y dispositivos móviles,
 * e intenta sincronizar la escena principal de ScreenV2.
 * 
 * @param {string|null} [roomId=null] - ID de la sala.
 * @param {number|null} [themeId=null] - ID opcional del tema.
 * @param {number|string|null} [screenId=null] - ID de pantalla gigante para ScreenV2.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Resultado de la proyección.
 */
export const ShowCover = async (roomId = null, themeId = null, screenId = null) => {
    try {
        const res = await ShowCoverDL(roomId, themeId);

        // Intento seguro de señalización a ScreenV2
        if (screenId) {
            try {
                await SetScreenV2MainDynamic(Number(screenId), 'FrontPageMemory');
            } catch (screenV2Error) {}
        }

        return res;
    } catch (error) {
        return {
            success: false,
            message: 'Error al proyectar la portada de Memorama.'
        };
    }
};

/**
 * Prepara la escena de juego 1 vs 1 de Memorama y genera los tableros barajados.
 * 
 * @param {string|null} [roomId=null] - ID de la sala.
 * @param {number|null} [themeId=null] - ID opcional del tema.
 * @param {number|string|null} [screenId=null] - ID de pantalla gigante para ScreenV2.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado de juego preparado.
 */
export const ShowGameScene = async (roomId = null, themeId = null, screenId = null) => {
    try {
        const res = await ShowGameSceneDL(roomId, themeId);

        // Intento seguro de señalización a ScreenV2
        if (screenId) {
            try {
                await SetScreenV2MainDynamic(Number(screenId), 'MemoryScreen');
            } catch (screenV2Error) {}
        }

        return res;
    } catch (error) {
        return {
            success: false,
            message: 'Error al preparar la escena de juego de Memorama.'
        };
    }
};

/**
 * Inicia la secuencia oficial de partida (conteo 3..2..1 -> preview -> playing).
 * 
 * @param {string|null} [roomId=null] - ID de la sala.
 * @param {Object} [matchOptions={}] - Opciones de configuración visibles en panel de operador.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado de la secuencia iniciada.
 */
export const StartMatch = async (roomId = null, matchOptions = {}) => {
    try {
        return await StartMatchDL(roomId, matchOptions);
    } catch (error) {
        return {
            success: false,
            message: 'Error al iniciar la secuencia de partida de Memorama.'
        };
    }
};

/**
 * Asigna a un participante a uno de los dos slots ("player1" o "player2") y persiste la elección en sessionStorage.
 * 
 * @param {string|null} [roomId=null] - ID de la sala.
 * @param {string} side - Lado elegido ("player1" o "player2").
 * @param {string} [playerName=''] - Nombre del participante.
 * @param {string} [connectionId=''] - ID de conexión SignalR.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado tras la asignación.
 */
export const SelectPlayerSide = async (roomId = null, side = 'player1', playerName = '', connectionId = '', accessPin = '') => {
    try {
        const cleanSide = String(side || 'player1').toLowerCase();
        const res = await SelectPlayerSideDL(roomId, cleanSide, playerName, connectionId, accessPin);

        if (res.success && roomId) {
            setStoredFanPlayer(roomId, cleanSide, playerName);
        }

        return res;
    } catch (error) {
        return {
            success: false,
            message: 'Error al seleccionar personaje en Memorama.'
        };
    }
};

/** Alias de SelectPlayerSide */
export const RegisterFanPlayer = SelectPlayerSide;

/**
 * Procesa el volteo autoritativo de una carta de Memorama en el backend.
 * 
 * @param {string|null} [roomId=null] - ID de la sala.
 * @param {string} playerSide - Lado del participante ("player1" o "player2").
 * @param {number} cardIndex - Índice de la carta (0 a 23).
 * @param {string|null} [connectionId=null] - ID de conexión SignalR.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Resultado del volteo y evaluación de acierto.
 */
export const FlipCard = async (roomId = null, playerSide = 'player1', cardIndex = 0, connectionId = null) => {
    try {
        if (cardIndex === undefined || cardIndex === null || isNaN(Number(cardIndex))) {
            return { success: false, message: 'El índice de carta es requerido.' };
        }

        return await FlipCardDL(roomId, playerSide, Number(cardIndex), connectionId);
    } catch (error) {
        return {
            success: false,
            message: 'Error al registrar el volteo de carta en Memorama.'
        };
    }
};

/**
 * Actualiza los nombres de los jugadores en caliente desde la consola de operador.
 * 
 * @param {string|null} [roomId=null] - ID de la sala.
 * @param {string|null} [player1Name=null] - Nombre para Jugador 1.
 * @param {string|null} [player2Name=null] - Nombre para Jugador 2.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado con nombres actualizados.
 */
export const UpdatePlayerNames = async (roomId = null, player1Name = null, player2Name = null) => {
    try {
        return await UpdatePlayerNamesDL(roomId, player1Name, player2Name);
    } catch (error) {
        return {
            success: false,
            message: 'Error al actualizar los nombres de los jugadores.'
        };
    }
};

/**
 * Control de emergencia: Ajusta el puntaje (+1 / -1 pt) de un participante.
 * 
 * @param {string|null} [roomId=null] - ID de la sala.
 * @param {string} playerSide - Lado del participante ("player1" o "player2").
 * @param {number} [delta=1] - Variación (+1 o -1).
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado con puntaje modificado.
 */
export const AdjustScore = async (roomId = null, playerSide = 'player1', delta = 1) => {
    try {
        return await AdjustScoreDL(roomId, playerSide, Number(delta) || 1);
    } catch (error) {
        return {
            success: false,
            message: 'Error al ajustar el puntaje en Memorama.'
        };
    }
};

/**
 * Control de emergencia / revancha: Reinicia la partida al estado 'game_ready' barajando nuevos tableros.
 * 
 * @param {string|null} [roomId=null] - ID de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado reiniciado.
 */
export const ResetMatch = async (roomId = null) => {
    try {
        return await ResetMatchDL(roomId);
    } catch (error) {
        return {
            success: false,
            message: 'Error al reiniciar la partida de Memorama.'
        };
    }
};

/** Alias de ResetMatch */
export const ResetGame = ResetMatch;

/**
 * Control de emergencia: Finaliza anticipadamente la partida declarando un ganador forzado o empate.
 * 
 * @param {string|null} [roomId=null] - ID de la sala.
 * @param {string|null} [winner=null] - Ganador ("player1", "player2" o "tie").
 * @param {string|null} [reason=null] - Motivo forzado.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado finalizado.
 */
export const FinishMatchEarly = async (roomId = null, winner = null, reason = null) => {
    try {
        return await FinishMatchEarlyDL(roomId, winner, reason);
    } catch (error) {
        return {
            success: false,
            message: 'Error al finalizar anticipadamente la partida de Memorama.'
        };
    }
};

/**
 * Procesa la salida voluntaria de un participante móvil de la partida y limpia la persistencia local.
 * 
 * @param {string|null} [roomId=null] - ID de la sala.
 * @param {string|null} [connectionId=null] - ID de conexión SignalR.
 * @param {string|null} [playerSide=null] - Lado liberado ("player1" o "player2").
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado tras la salida.
 */
export const LeaveGame = async (roomId = null, connectionId = null, playerSide = null) => {
    try {
        if (roomId) {
            clearStoredFanPlayer(roomId);
        }
        return await LeaveGameDL(roomId, connectionId, playerSide);
    } catch (error) {
        return {
            success: false,
            message: 'Error al procesar el abandono de la partida.'
        };
    }
};

/** Alias de LeaveGame */
export const UnregisterFanPlayer = LeaveGame;

// =========================================================================
// SEGURIDAD, TOKENS DE ACCESO Y AUTORIZACIÓN POR SALA (3 HORAS)
// =========================================================================

const MEMORY_AUTH_DURATION_MS = 3 * 60 * 60 * 1000; // 3 Horas de vigencia en sessionStorage

/**
 * Codifica y ofusca el roomId y PIN en un token compacto para QR / URLs.
 * 
 * @param {string} roomId - Identificador GUID de la sala.
 * @param {string} [pin=''] - PIN de acceso numérico.
 * @returns {string} Token ofuscado seguro para URLs.
 */
export const encodeAccessCode = (roomId, pin = '') => {
    if (!roomId) return '';
    try {
        const raw = JSON.stringify({ r: String(roomId), p: String(pin || '') });
        if (typeof window !== 'undefined' && window.btoa) {
            return btoa(unescape(encodeURIComponent(raw)))
                .replace(/\+/g, '-')
                .replace(/\//g, '_')
                .replace(/=+$/, '');
        }
        return Buffer.from(raw).toString('base64url');
    } catch (e) {
        return '';
    }
};

/**
 * Desofusca un token recibido por parámetro de URL.
 * 
 * @param {string} token - Token ofuscado.
 * @returns {{roomId: string, pin: string}|null} Datos descifrados o null si es inválido.
 */
export const decodeAccessCode = (token) => {
    if (!token) return null;
    try {
        let base64 = token.replace(/-/g, '+').replace(/_/g, '/');
        while (base64.length % 4) base64 += '=';
        let jsonStr = '';
        if (typeof window !== 'undefined' && window.atob) {
            jsonStr = decodeURIComponent(escape(atob(base64)));
        } else {
            jsonStr = Buffer.from(base64, 'base64').toString('utf8');
        }
        const parsed = JSON.parse(jsonStr);
        return {
            roomId: String(parsed.r || ''),
            pin: String(parsed.p || '')
        };
    } catch (e) {
        return null;
    }
};

/**
 * Obtiene la autorización de Memorama guardada en sessionStorage.
 * Si pasaron más de 3 horas, la invalida y retorna null.
 * 
 * @param {string|null} [roomId=null] - Identificador opcional de sala.
 * @returns {{authorized: boolean, roomId: string, pin: string, expiresAt: number}|null}
 */
export const getMemoryAuth = (roomId = null) => {
    try {
        if (typeof window === 'undefined' || !window.sessionStorage) return null;
        const key = roomId ? `memory_auth_${roomId}` : 'memory_auth';
        const raw = sessionStorage.getItem(key) || sessionStorage.getItem('memory_auth');
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (parsed.expiresAt && Date.now() > parsed.expiresAt) {
            clearMemoryAuth(roomId);
            return null;
        }
        return parsed;
    } catch (e) {
        return null;
    }
};

/**
 * Guarda la autorización del participante en sessionStorage con caducidad de 3 horas.
 * 
 * @param {string} roomId - Identificador de sala.
 * @param {string} [pin=''] - PIN validado.
 */
export const setMemoryAuth = (roomId, pin = '') => {
    try {
        if (typeof window === 'undefined' || !window.sessionStorage) return;
        const payload = {
            authorized: true,
            roomId: String(roomId || ''),
            pin: String(pin || ''),
            savedAt: Date.now(),
            expiresAt: Date.now() + MEMORY_AUTH_DURATION_MS
        };
        const key = roomId ? `memory_auth_${roomId}` : 'memory_auth';
        sessionStorage.setItem(key, JSON.stringify(payload));
        sessionStorage.setItem('memory_auth', JSON.stringify(payload));
    } catch (e) {}
};

/**
 * Elimina la autorización del participante del sessionStorage.
 * 
 * @param {string|null} [roomId=null] - Identificador de sala.
 */
export const clearMemoryAuth = (roomId = null) => {
    try {
        if (typeof window === 'undefined' || !window.sessionStorage) return;
        if (roomId) {
            sessionStorage.removeItem(`memory_auth_${roomId}`);
        }
        sessionStorage.removeItem('memory_auth');
    } catch (e) {}
};

/**
 * Libera ambos lugares de jugadores (desconecta a J1 y J2 en el servidor).
 * 
 * @param {string|null} [roomId=null] - ID de sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const ClearPlayers = async (roomId = null) => {
    try {
        return await ClearPlayersDL(roomId);
    } catch (error) {
        return {
            success: false,
            data: null,
            message: 'Error al solicitar la liberación de jugadores.'
        };
    }
};

/**
 * Actualiza o regenera el PIN de acceso a la sala de Memorama.
 * 
 * @param {string|null} [roomId=null] - ID de sala.
 * @param {string|null} [newPin=null] - Nuevo PIN opcional.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const ChangeAccessPin = async (roomId = null, newPin = null) => {
    try {
        return await ChangeAccessPinDL(roomId, newPin);
    } catch (error) {
        return {
            success: false,
            data: null,
            message: 'Error al cambiar el PIN de acceso.'
        };
    }
};

export default {
    // Web Audio Synthesizer
    isAudioSupported,
    getAudioContext,
    unlockAudioContext,
    initAudioSynth,
    setAudioMuted,
    isAudioMuted,
    toggleAudioMute,
    playTone,
    playFlipSound,
    playMatchSuccessSound,
    playMatchFailedSound,
    playVictoryFanfare,
    playCountdownBeep,

    // Haptics
    triggerHapticFeedback,
    hapticFlip,
    hapticMatchSuccess,
    hapticMatchFailed,
    hapticVictory,

    // Proactive Image Preloading
    preloadImages,
    resolveMediaUrl,
    getResolvedMediaUrl,

    // SessionStorage Helpers
    getStoredFanPlayer,
    setStoredFanPlayer,
    clearStoredFanPlayer,

    // Sala y Configuración
    GetActiveRoom,

    // Temas y Barajas
    GetThemes,
    GetThemeById,
    GetTheme,
    SaveTheme,
    CreateTheme,
    UpdateTheme,
    DeleteTheme,
    CloneTheme,

    // Configuración
    GetConfig,
    SaveConfig,

    // Historial
    GetMatchHistory,
    SaveMatchResult,

    // Multimedia
    UploadAsset,
    UploadBatchCards,

    // Control de Partida
    GetMatchState,
    ShowCover,
    ShowGameScene,
    StartMatch,
    SelectPlayerSide,
    RegisterFanPlayer,
    FlipCard,
    UpdatePlayerNames,
    AdjustScore,
    ResetMatch,
    ResetGame,
    FinishMatchEarly,
    LeaveGame,
    UnregisterFanPlayer,
    encodeAccessCode,
    decodeAccessCode,
    getMemoryAuth,
    setMemoryAuth,
    clearMemoryAuth,
    ClearPlayers,
    ChangeAccessPin
};
